import type { Role } from './auth/types';

/** Portal home path for each role. */
export const ROLE_HOME: Record<Role, string> = {
  super_admin: '/royal-overseas',
  school_admin: '/admin',
  teacher: '/admin',
  counselor: '/counselor',
  parent: '/parent',
  student: '/student',
  university: '/university',
  employer: '/employer',
  agent: '/counselor',
};

/** Route prefixes each role may enter. */
export const ROLE_ALLOWED_PREFIXES: Record<Role, string[]> = {
  super_admin: ['/royal-overseas', '/account'],
  school_admin: ['/admin', '/account'],
  teacher: ['/admin', '/account'],
  counselor: ['/counselor', '/account'],
  parent: ['/parent', '/account'],
  student: ['/student', '/account'],
  university: ['/university', '/account'],
  employer: ['/employer', '/account'],
  agent: ['/counselor', '/account'],
};

export function roleHomePath(role: Role | null | undefined): string {
  if (!role) return '/login';
  return ROLE_HOME[role] ?? '/login';
}

export function canAccessPath(role: Role | null | undefined, pathname: string): boolean {
  if (!role) return false;
  const prefixes = ROLE_ALLOWED_PREFIXES[role] ?? [];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function hasRole(
  userRole: Role | null | undefined,
  allowed: Role | Role[],
): boolean {
  if (!userRole) return false;
  const list = Array.isArray(allowed) ? allowed : [allowed];
  return list.includes(userRole);
}

/** Client-side action checks (defense in depth; backend still enforces). */
export type Action =
  | 'students:read'
  | 'students:write'
  | 'students:delete'
  | 'academics:write'
  | 'attendance:write'
  | 'career:write'
  | 'scholarships:write'
  | 'admissions:write'
  | 'admissions:decide'
  | 'university:write'
  | 'jobs:write'
  | 'notifications:send'
  | 'admin:schools'
  | 'admin:users'
  | 'reports:academic'
  | 'reports:career';

const ACTION_ROLES: Record<Action, Role[]> = {
  'students:read': [
    'super_admin',
    'school_admin',
    'teacher',
    'counselor',
    'parent',
    'student',
    'university',
    'employer',
  ],
  'students:write': ['school_admin'],
  'students:delete': ['school_admin'],
  'academics:write': ['school_admin', 'teacher'],
  'attendance:write': ['school_admin', 'teacher'],
  'career:write': ['counselor'],
  'scholarships:write': ['school_admin', 'counselor', 'student'],
  'admissions:write': ['school_admin', 'counselor'],
  'admissions:decide': ['university'],
  'university:write': ['university'],
  'jobs:write': ['employer', 'school_admin', 'counselor', 'student'],
  'notifications:send': ['school_admin', 'teacher', 'counselor', 'university', 'employer'],
  'admin:schools': ['super_admin'],
  'admin:users': ['school_admin', 'super_admin'],
  'reports:academic': ['super_admin', 'school_admin', 'teacher', 'counselor', 'parent'],
  'reports:career': ['super_admin', 'school_admin', 'counselor', 'parent'],
};

export function canPerform(role: Role | null | undefined, action: Action): boolean {
  if (!role) return false;
  return (ACTION_ROLES[action] ?? []).includes(role);
}
